import { Injectable } from '@angular/core';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { Router } from '@angular/router';
import { Cache } from 'core/models/cache';
import { filterCourses } from './courses-filters';

@Injectable()
export class CoursesListService {
  userRole: any;

  constructor(
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public router: Router
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  getDatasource(listId, options: any = {}, coursesCache: Cache) {
    const dataSource = new TableDatasource({
      sort: 'title',
      columns: this.getColumns(options),
      docs: coursesCache.data,
      page: 1,
      pages: 1,
      limit: coursesCache.data.length,
      total: coursesCache.data.length,
      load: (props) => this.fetch(props, coursesCache),
      ...options
    });
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch(options, coursesCache: Cache) {
    return coursesCache.fetch(options).then((courses) => filterCourses(courses, options));
  }

  getColumns(options: any = {}) {
    const { selectedColumns } = options;
    const columns = [
      {
        source: 'title',
        label: this.translate.instant('courses.headerTable.name'),
        sortable: true
      },
      {
        source: 'clientName',
        label: this.translate.instant('courses.headerTable.creator'),
        sortable: true
      },
      {
        source: 'topic',
        label: this.translate.instant('courses.headerTable.topic'),
        sortable: true
      },
      {
        source: 'courses',
        dataType: DataTypes.number,
        label: this.translate.instant('courses.headerTable.numCourses'),
        sortable: true,
        valueParser: (value) => (value ? value.length : 0)
      },
      {
        source: 'duration',
        label: this.translate.instant('courses.headerTable.duration'),
        sortable: true,
        valueParser: (value, course) => this.listHelper.parseCourseDuration(course)
      },
      {
        source: 'shouldSendDiploma',
        dataType: DataTypes.boolean,
        label: this.translate.instant('games.properties.shouldSendDiploma'),
        sortable: false,
        valueParser: (value, course) => course.configuration?.shouldSendDiploma
      },
      {
        source: 'credits',
        label: this.translate.instant('courses.headerTable.credits'),
        sortable: true,
        valueParser: (value) => (value ? this.translate.instant('courses.price', { credits: value }) : '')
      },
      {
        source: 'accessMode',
        label: this.translate.instant('courses.headerTable.access'),
        visible: !!options.defaultAccessMode,
        sortable: true,
        valueParser: (value) => {
          const accessMode = value || options.defaultAccessMode;
          const defaultAccessMode = this.translate.instant(`courses.config.accessModes.${options.defaultAccessMode}`, {
            mode: options.defaultAccessMode
          });
          return this.translate.instant(`courses.config.accessModes.${accessMode}`, { mode: defaultAccessMode });
        }
      },
      {
        source: 'status',
        label: this.translate.instant('itineraries.headerTable.status'),
        valueParser: (value, course) => {
          if (course.withdrawn) return this.translate.instant('itineraries.withdrawnItinerary');
          return this.translate.instant(`itineraries.${course.publish.active ? 'publishLabel' : 'draftLabel'}`);
        }
      },
      {
        source: 'languages',
        label: this.translate.instant('nouns.languages')
      },
      {
        source: 'description',
        label: this.translate.instant('courses.headerTable.description'),
        sortable: true
      }
    ];
    if (selectedColumns && selectedColumns.length) {
      return selectedColumns.map((colName) => columns.find((column) => column.source === colName));
    }
    return columns;
  }
}
